import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  IconButton,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  Refresh,
  Pets,
  LocalHospital,
  Medication,
  CalendarToday,
  Group,
  Warning,
  Agriculture,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const DashboardHome = () => {
  const API_BASE_URL = "https://dairy.server.statify.co.ke/";

  const [stats, setStats] = useState({
    totalAnimals: 0,
    activeAnimals: 0,
    inactiveAnimals: 0,
    draftAnimals: 0,
    totalMilk: 0,
    totalSessions: 0,
    totalEmployees: 0,
    totalMedications: 0,
    totalHealthRecords: 0,
    totalCalvingEvents: 0,
    totalReproductionEvents: 0,
    lowStockMedications: 0,
    upcomingCalvings: 0,
  });

  const [chartData, setChartData] = useState({
    milkProduction: [],
    animalHealth: [],
    medicationUsage: [],
    reproductionStats: [],
    animalBreakdown: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Updated color scheme
  const PRIMARY_COLOR = "#024A59";
  const SECONDARY_COLOR = "#FFA916";
  const COLORS = ["#024A59", "#FFA916", "#03718C", "#FFBA4D", "#025A6B"];
  const HEALTH_COLORS = ["#024A59", "#FFA916", "#03718C"];
  const ANIMAL_STATUS_COLORS = ["#024A59", "#FFA916", "#03718C"];

  const fetchDashboardData = async () => {
    try {
      setError(null);

      const [
        animalsRes,
        milkingStatsRes,
        employeesRes,
        medicationsRes,
        healthRecordsRes,
        calvingEventsRes,
        reproductionEventsRes,
        medicationUsageRes,
      ] = await Promise.all([
        fetch(`${API_BASE_URL}/api/animals`),
        fetch(`${API_BASE_URL}/api/milking/stats`),
        fetch(`${API_BASE_URL}/api/employees`),
        fetch(`${API_BASE_URL}/api/medications`),
        fetch(`${API_BASE_URL}/api/health-records`),
        fetch(`${API_BASE_URL}/api/calving-events`),
        fetch(`${API_BASE_URL}/api/reproduction-events`),
        fetch(`${API_BASE_URL}/api/medication-usage`),
      ]);

      const responses = [
        animalsRes,
        milkingStatsRes,
        employeesRes,
        medicationsRes,
        healthRecordsRes,
        calvingEventsRes,
        reproductionEventsRes,
      ];

      if (responses.some((r) => !r.ok)) throw new Error("Failed to load some data");

      const [
        animalsData,
        milkingStats,
        employeesData,
        medicationsData,
        healthRecordsData,
        calvingEventsData,
        reproductionEventsData,
        medicationUsageData,
      ] = await Promise.all(responses.map((r) => r.json()).concat([medicationUsageRes.ok ? medicationUsageRes.json() : []]));

      const animalsArray = Array.isArray(animalsData)
        ? animalsData
        : animalsData.data || [];

      const activeAnimals = animalsArray.filter(
        (a) => a.status?.toLowerCase() === "active" || a.is_active
      ).length;
      const inactiveAnimals = animalsArray.filter(
        (a) => a.status?.toLowerCase() === "inactive" || !a.is_active
      ).length;
      const draftAnimals = animalsArray.filter(
        (a) => a.status?.toLowerCase() === "draft"
      ).length;

      const lowStockMeds = Array.isArray(medicationsData)
        ? medicationsData.filter((m) => m.quantity < (m.min_stock_level || 10)).length
        : 0;

      const upcomingCalvings = Array.isArray(calvingEventsData)
        ? calvingEventsData.filter((event) => {
            const eventDate = new Date(event.expected_date || event.date);
            const today = new Date();
            const diffDays = (eventDate - today) / (1000 * 60 * 60 * 24);
            return diffDays >= 0 && diffDays <= 30;
          }).length
        : 0;

      setStats({
        totalAnimals: animalsArray.length,
        activeAnimals,
        inactiveAnimals,
        draftAnimals,
        totalMilk: milkingStats.total_milk_produced || milkingStats.total_milk || 0,
        totalSessions: milkingStats.total_sessions || 0,
        totalEmployees:
          employeesData.count || (Array.isArray(employeesData) ? employeesData.length : 0),
        totalMedications: Array.isArray(medicationsData) ? medicationsData.length : 0,
        totalHealthRecords: Array.isArray(healthRecordsData)
          ? healthRecordsData.length
          : 0,
        totalCalvingEvents: Array.isArray(calvingEventsData)
          ? calvingEventsData.length
          : 0,
        totalReproductionEvents: Array.isArray(reproductionEventsData)
          ? reproductionEventsData.length
          : 0,
        lowStockMedications: lowStockMeds,
        upcomingCalvings,
      });

      generateChartData(
        milkingStats,
        healthRecordsData,
        medicationsData,
        reproductionEventsData,
        medicationUsageData,
        { activeAnimals, inactiveAnimals, draftAnimals }
      );

      setLastUpdated(new Date());
    } catch (err) {
      console.error("Dashboard data fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateChartData = (
    milkingStats,
    healthRecords,
    medications,
    reproductionEvents,
    medicationUsage,
    animalStats
  ) => {
    const milkData = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - i));
      return {
        name: date.toLocaleDateString("en-US", { weekday: "short" }),
        milk: milkingStats.daily_production?.[i] || Math.floor(Math.random() * 100) + 50,
        sessions: milkingStats.daily_sessions?.[i] || Math.floor(Math.random() * 10) + 5,
      };
    });

    const totalAnimals =
      animalStats.activeAnimals + animalStats.inactiveAnimals + animalStats.draftAnimals;
    const healthRecordsArray = Array.isArray(healthRecords) ? healthRecords : [];

    const healthData = [
      {
        name: "Healthy",
        value: Math.max(0, totalAnimals - healthRecordsArray.length),
        color: PRIMARY_COLOR,
      },
      {
        name: "Under Treatment",
        value: healthRecordsArray.filter((r) =>
          ["pending", "treatment", "sick"].some((s) =>
            r.status?.toLowerCase().includes(s)
          )
        ).length,
        color: SECONDARY_COLOR,
      },
      {
        name: "Recovered",
        value: healthRecordsArray.filter((r) =>
          ["completed", "recovered", "healthy"].some((s) =>
            r.status?.toLowerCase().includes(s)
          )
        ).length,
        color: "#03718C",
      },
    ];

    const animalBreakdownData = [
      { name: "Active", value: animalStats.activeAnimals, color: PRIMARY_COLOR },
      { name: "Inactive", value: animalStats.inactiveAnimals, color: SECONDARY_COLOR },
      { name: "Draft", value: animalStats.draftAnimals, color: "#03718C" },
    ];

    const medicationData =
      Array.isArray(medicationUsage) && medicationUsage.length > 0
        ? medicationUsage.slice(0, 5).map((m) => ({
            name: m.medication_name?.substring(0, 12) || "Medication",
            usage: m.quantity_used || m.amount || 0,
          }))
        : [];

    const reproductionEventsArray = Array.isArray(reproductionEvents)
      ? reproductionEvents
      : [];

    const reproductionData = [
      {
        name: "Heat",
        value: reproductionEventsArray.filter((e) =>
          ["heat"].some((t) => e.event_type?.toLowerCase().includes(t))
        ).length,
      },
      {
        name: "Insemination",
        value: reproductionEventsArray.filter((e) =>
          ["insemination"].some((t) => e.event_type?.toLowerCase().includes(t))
        ).length,
      },
      {
        name: "Pregnancy Check",
        value: reproductionEventsArray.filter((e) =>
          ["pregnancy"].some((t) => e.event_type?.toLowerCase().includes(t))
        ).length,
      },
      {
        name: "Calving",
        value: reproductionEventsArray.filter((e) =>
          ["calving"].some((t) => e.event_type?.toLowerCase().includes(t))
        ).length,
      },
    ].filter((r) => r.value > 0);

    setChartData({
      milkProduction: milkData,
      animalHealth: healthData,
      animalBreakdown: animalBreakdownData,
      medicationUsage: medicationData,
      reproductionStats: reproductionData,
    });
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 5 * 60 * 1000); // Auto refresh every 5 min
    return () => clearInterval(interval);
  }, []);

  const StatCard = ({ title, value, icon, color, trend, subtitle }) => (
    <Card
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
        color: "white",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        textAlign: "left",
        transition: "0.2s",
        "&:hover": { transform: "scale(1.02)" },
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {value}
            </Typography>
            <Typography variant="subtitle2">{title}</Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ backgroundColor: "rgba(255,255,255,0.2)", borderRadius: "50%", p: 1 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const ChartCard = ({ title, children, height = 300 }) => (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="bold" color={PRIMARY_COLOR}>
          {title}
        </Typography>
        <Box sx={{ height, width: "100%" }}>{children}</Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <CircularProgress sx={{ color: PRIMARY_COLOR }} />
        <Typography sx={{ mt: 2, color: PRIMARY_COLOR }}>Loading dashboard data...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: 3,
        overflow: "hidden",
        minHeight: "100vh",
        backgroundColor: "#f9fafc",
      }}
    >
      <Paper
        sx={{
          p: 3,
          mb: 3,
          background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`,
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Farm Management Dashboard
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            Overview of dairy operations
          </Typography>
          <Chip
            label={`Last updated: ${lastUpdated.toLocaleTimeString()}`}
            sx={{ backgroundColor: "rgba(255,255,255,0.2)", mt: 1, color: "white" }}
          />
        </Box>
        <IconButton
          onClick={fetchDashboardData}
          sx={{ color: "white", backgroundColor: "rgba(255,255,255,0.2)" }}
        >
          <Refresh />
        </IconButton>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Main Stats Row */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Animals" value={stats.totalAnimals} icon={<Pets />} color={PRIMARY_COLOR} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Milk Production" value={`${stats.totalMilk}L`} subtitle="Last 30 days" icon={<TrendingUp />} color={SECONDARY_COLOR} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Employees" value={stats.totalEmployees} icon={<Group />} color={PRIMARY_COLOR} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Health Records" value={stats.totalHealthRecords} icon={<LocalHospital />} color={SECONDARY_COLOR} />
        </Grid>
      </Grid>

      {/* Charts and Secondary Stats */}
      <Grid container spacing={2} sx={{ mt: 1, height: "calc(100vh - 380px)" }}>
        <Grid item xs={12} md={8}>
          <ChartCard title="Milk Production Trend (Last 7 Days)" height={300}>
            <ResponsiveContainer>
              <LineChart data={chartData.milkProduction}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="milk" stroke={PRIMARY_COLOR} strokeWidth={3} name="Milk (L)" />
                <Line type="monotone" dataKey="sessions" stroke={SECONDARY_COLOR} strokeWidth={2} name="Sessions" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ChartCard title="Animal Health Status" height={300}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={chartData.animalHealth}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {chartData.animalHealth.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ChartCard title="Animal Status Breakdown" height={250}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={chartData.animalBreakdown}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {chartData.animalBreakdown.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ChartCard title="Top Medication Usage" height={250}>
            <ResponsiveContainer>
              <BarChart data={chartData.medicationUsage}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="usage" fill={PRIMARY_COLOR} name="Quantity Used" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ChartCard title="Reproduction Events" height={250}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={chartData.reproductionStats}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {chartData.reproductionStats.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardHome;

// import React, { useEffect, useState } from "react";
// import {
//   Box,
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   CircularProgress,
//   Alert,
//   Paper,
//   Chip,
//   IconButton,
// } from "@mui/material";
// import {
//   TrendingUp,
//   TrendingDown,
//   Refresh,
//   Pets,
//   LocalHospital,
//   Medication,
//   CalendarToday,
//   Group,
//   Warning,
//   Agriculture,
// } from "@mui/icons-material";
// import {
//   LineChart,
//   Line,
//   BarChart,
//   Bar,
//   PieChart,
//   Pie,
//   Cell,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   Legend,
//   ResponsiveContainer,
// } from "recharts";

// const DashboardHome = () => {
//   const API_BASE_URL = "https://dairy.server.statify.co.ke/";

//   const [stats, setStats] = useState({
//     totalAnimals: 0,
//     activeAnimals: 0,
//     inactiveAnimals: 0,
//     draftAnimals: 0,
//     totalMilk: 0,
//     totalSessions: 0,
//     totalEmployees: 0,
//     totalMedications: 0,
//     totalHealthRecords: 0,
//     totalCalvingEvents: 0,
//     totalReproductionEvents: 0,
//     lowStockMedications: 0,
//     upcomingCalvings: 0,
//   });

//   const [chartData, setChartData] = useState({
//     milkProduction: [],
//     animalHealth: [],
//     medicationUsage: [],
//     reproductionStats: [],
//     animalBreakdown: [],
//   });

//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [lastUpdated, setLastUpdated] = useState(new Date());

//   const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];
//   const HEALTH_COLORS = ["#00C49F", "#FF8042", "#FFBB28"];
//   const ANIMAL_STATUS_COLORS = ["#2ecc71", "#e74c3c", "#95a5a6"];

//   const fetchDashboardData = async () => {
//     try {
//       setError(null);

//       const [
//         animalsRes,
//         milkingStatsRes,
//         employeesRes,
//         medicationsRes,
//         healthRecordsRes,
//         calvingEventsRes,
//         reproductionEventsRes,
//         medicationUsageRes,
//       ] = await Promise.all([
//         fetch(`${API_BASE_URL}/api/animals`),
//         fetch(`${API_BASE_URL}/api/milking/stats`),
//         fetch(`${API_BASE_URL}/api/employees`),
//         fetch(`${API_BASE_URL}/api/medications`),
//         fetch(`${API_BASE_URL}/api/health-records`),
//         fetch(`${API_BASE_URL}/api/calving-events`),
//         fetch(`${API_BASE_URL}/api/reproduction-events`),
//         fetch(`${API_BASE_URL}/api/medication-usage`),
//       ]);

//       const responses = [
//         animalsRes,
//         milkingStatsRes,
//         employeesRes,
//         medicationsRes,
//         healthRecordsRes,
//         calvingEventsRes,
//         reproductionEventsRes,
//       ];

//       if (responses.some((r) => !r.ok)) throw new Error("Failed to load some data");

//       const [
//         animalsData,
//         milkingStats,
//         employeesData,
//         medicationsData,
//         healthRecordsData,
//         calvingEventsData,
//         reproductionEventsData,
//         medicationUsageData,
//       ] = await Promise.all(responses.map((r) => r.json()).concat([medicationUsageRes.ok ? medicationUsageRes.json() : []]));

//       const animalsArray = Array.isArray(animalsData)
//         ? animalsData
//         : animalsData.data || [];

//       const activeAnimals = animalsArray.filter(
//         (a) => a.status?.toLowerCase() === "active" || a.is_active
//       ).length;
//       const inactiveAnimals = animalsArray.filter(
//         (a) => a.status?.toLowerCase() === "inactive" || !a.is_active
//       ).length;
//       const draftAnimals = animalsArray.filter(
//         (a) => a.status?.toLowerCase() === "draft"
//       ).length;

//       const lowStockMeds = Array.isArray(medicationsData)
//         ? medicationsData.filter((m) => m.quantity < (m.min_stock_level || 10)).length
//         : 0;

//       const upcomingCalvings = Array.isArray(calvingEventsData)
//         ? calvingEventsData.filter((event) => {
//             const eventDate = new Date(event.expected_date || event.date);
//             const today = new Date();
//             const diffDays = (eventDate - today) / (1000 * 60 * 60 * 24);
//             return diffDays >= 0 && diffDays <= 30;
//           }).length
//         : 0;

//       setStats({
//         totalAnimals: animalsArray.length,
//         activeAnimals,
//         inactiveAnimals,
//         draftAnimals,
//         totalMilk: milkingStats.total_milk_produced || milkingStats.total_milk || 0,
//         totalSessions: milkingStats.total_sessions || 0,
//         totalEmployees:
//           employeesData.count || (Array.isArray(employeesData) ? employeesData.length : 0),
//         totalMedications: Array.isArray(medicationsData) ? medicationsData.length : 0,
//         totalHealthRecords: Array.isArray(healthRecordsData)
//           ? healthRecordsData.length
//           : 0,
//         totalCalvingEvents: Array.isArray(calvingEventsData)
//           ? calvingEventsData.length
//           : 0,
//         totalReproductionEvents: Array.isArray(reproductionEventsData)
//           ? reproductionEventsData.length
//           : 0,
//         lowStockMedications: lowStockMeds,
//         upcomingCalvings,
//       });

//       generateChartData(
//         milkingStats,
//         healthRecordsData,
//         medicationsData,
//         reproductionEventsData,
//         medicationUsageData,
//         { activeAnimals, inactiveAnimals, draftAnimals }
//       );

//       setLastUpdated(new Date());
//     } catch (err) {
//       console.error("Dashboard data fetch error:", err);
//       setError(err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const generateChartData = (
//     milkingStats,
//     healthRecords,
//     medications,
//     reproductionEvents,
//     medicationUsage,
//     animalStats
//   ) => {
//     const milkData = Array.from({ length: 7 }, (_, i) => {
//       const date = new Date();
//       date.setDate(date.getDate() - (6 - i));
//       return {
//         name: date.toLocaleDateString("en-US", { weekday: "short" }),
//         milk: milkingStats.daily_production?.[i] || Math.floor(Math.random() * 100) + 50,
//         sessions: milkingStats.daily_sessions?.[i] || Math.floor(Math.random() * 10) + 5,
//       };
//     });

//     const totalAnimals =
//       animalStats.activeAnimals + animalStats.inactiveAnimals + animalStats.draftAnimals;
//     const healthRecordsArray = Array.isArray(healthRecords) ? healthRecords : [];

//     const healthData = [
//       {
//         name: "Healthy",
//         value: Math.max(0, totalAnimals - healthRecordsArray.length),
//         color: "#00C49F",
//       },
//       {
//         name: "Under Treatment",
//         value: healthRecordsArray.filter((r) =>
//           ["pending", "treatment", "sick"].some((s) =>
//             r.status?.toLowerCase().includes(s)
//           )
//         ).length,
//         color: "#FF8042",
//       },
//       {
//         name: "Recovered",
//         value: healthRecordsArray.filter((r) =>
//           ["completed", "recovered", "healthy"].some((s) =>
//             r.status?.toLowerCase().includes(s)
//           )
//         ).length,
//         color: "#FFBB28",
//       },
//     ];

//     const animalBreakdownData = [
//       { name: "Active", value: animalStats.activeAnimals, color: "#2ecc71" },
//       { name: "Inactive", value: animalStats.inactiveAnimals, color: "#e74c3c" },
//       { name: "Draft", value: animalStats.draftAnimals, color: "#95a5a6" },
//     ];

//     const medicationData =
//       Array.isArray(medicationUsage) && medicationUsage.length > 0
//         ? medicationUsage.slice(0, 5).map((m) => ({
//             name: m.medication_name?.substring(0, 12) || "Medication",
//             usage: m.quantity_used || m.amount || 0,
//           }))
//         : [];

//     const reproductionEventsArray = Array.isArray(reproductionEvents)
//       ? reproductionEvents
//       : [];

//     const reproductionData = [
//       {
//         name: "Heat",
//         value: reproductionEventsArray.filter((e) =>
//           ["heat"].some((t) => e.event_type?.toLowerCase().includes(t))
//         ).length,
//       },
//       {
//         name: "Insemination",
//         value: reproductionEventsArray.filter((e) =>
//           ["insemination"].some((t) => e.event_type?.toLowerCase().includes(t))
//         ).length,
//       },
//       {
//         name: "Pregnancy Check",
//         value: reproductionEventsArray.filter((e) =>
//           ["pregnancy"].some((t) => e.event_type?.toLowerCase().includes(t))
//         ).length,
//       },
//       {
//         name: "Calving",
//         value: reproductionEventsArray.filter((e) =>
//           ["calving"].some((t) => e.event_type?.toLowerCase().includes(t))
//         ).length,
//       },
//     ].filter((r) => r.value > 0);

//     setChartData({
//       milkProduction: milkData,
//       animalHealth: healthData,
//       animalBreakdown: animalBreakdownData,
//       medicationUsage: medicationData,
//       reproductionStats: reproductionData,
//     });
//   };

//   useEffect(() => {
//     fetchDashboardData();
//     const interval = setInterval(fetchDashboardData, 5 * 60 * 1000); // Auto refresh every 5 min
//     return () => clearInterval(interval);
//   }, []);

//   const StatCard = ({ title, value, icon, color, trend, subtitle }) => (
//     <Card
//       sx={{
//         height: "100%",
//         background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
//         color: "white",
//         display: "flex",
//         flexDirection: "column",
//         justifyContent: "center",
//         textAlign: "left",
//         transition: "0.2s",
//         "&:hover": { transform: "scale(1.02)" },
//       }}
//     >
//       <CardContent>
//         <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
//           <Box>
//             <Typography variant="h4" fontWeight="bold">
//               {value}
//             </Typography>
//             <Typography variant="subtitle2">{title}</Typography>
//             {subtitle && (
//               <Typography variant="caption" sx={{ opacity: 0.8 }}>
//                 {subtitle}
//               </Typography>
//             )}
//           </Box>
//           <Box sx={{ backgroundColor: "rgba(255,255,255,0.2)", borderRadius: "50%", p: 1 }}>
//             {icon}
//           </Box>
//         </Box>
//       </CardContent>
//     </Card>
//   );

//   const ChartCard = ({ title, children, height = 300 }) => (
//     <Card sx={{ height: "100%" }}>
//       <CardContent>
//         <Typography variant="h6" gutterBottom fontWeight="bold">
//           {title}
//         </Typography>
//         <Box sx={{ height, width: "100%" }}>{children}</Box>
//       </CardContent>
//     </Card>
//   );

//   if (loading) {
//     return (
//       <Box
//         sx={{
//           height: "100vh",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//           flexDirection: "column",
//         }}
//       >
//         <CircularProgress />
//         <Typography sx={{ mt: 2 }}>Loading dashboard data...</Typography>
//       </Box>
//     );
//   }

//   return (
//     <Box
//       sx={{
//         flexGrow: 1,
//         p: 3,
//         overflow: "hidden",
//         minHeight: "100vh",
//         backgroundColor: "#f9fafc",
//       }}
//     >
//       <Paper
//         sx={{
//           p: 3,
//           mb: 3,
//           background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//           color: "white",
//           display: "flex",
//           justifyContent: "space-between",
//           alignItems: "center",
//         }}
//       >
//         <Box>
//           <Typography variant="h4" fontWeight="bold">
//             Farm Management Dashboard
//           </Typography>
//           <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
//             Overview of dairy operations
//           </Typography>
//           <Chip
//             label={`Last updated: ${lastUpdated.toLocaleTimeString()}`}
//             sx={{ backgroundColor: "rgba(255,255,255,0.2)", mt: 1 }}
//           />
//         </Box>
//         <IconButton
//           onClick={fetchDashboardData}
//           sx={{ color: "white", backgroundColor: "rgba(255,255,255,0.2)" }}
//         >
//           <Refresh />
//         </IconButton>
//       </Paper>

//       {error && (
//         <Alert severity="error" sx={{ mb: 2 }}>
//           {error}
//         </Alert>
//       )}

//       {/* Main Stats Row */}
//       <Grid container spacing={2}>
//         <Grid item xs={12} sm={6} md={3}>
//           <StatCard title="Total Animals" value={stats.totalAnimals} icon={<Pets />} color="#3498db" />
//         </Grid>
//         <Grid item xs={12} sm={6} md={3}>
//           <StatCard title="Milk Production" value={`${stats.totalMilk}L`} subtitle="Last 30 days" icon={<TrendingUp />} color="#2ecc71" />
//         </Grid>
//         <Grid item xs={12} sm={6} md={3}>
//           <StatCard title="Employees" value={stats.totalEmployees} icon={<Group />} color="#9b59b6" />
//         </Grid>
//         <Grid item xs={12} sm={6} md={3}>
//           <StatCard title="Health Records" value={stats.totalHealthRecords} icon={<LocalHospital />} color="#e74c3c" />
//         </Grid>
//       </Grid>

//       {/* Charts and Secondary Stats */}
//       <Grid container spacing={2} sx={{ mt: 1, height: "calc(100vh - 380px)" }}>
//         <Grid item xs={12} md={8}>
//           <ChartCard title="Milk Production Trend (Last 7 Days)" height={300}>
//             <ResponsiveContainer>
//               <LineChart data={chartData.milkProduction}>
//                 <CartesianGrid strokeDasharray="3 3" />
//                 <XAxis dataKey="name" />
//                 <YAxis />
//                 <Tooltip />
//                 <Legend />
//                 <Line type="monotone" dataKey="milk" stroke="#3498db" strokeWidth={3} name="Milk (L)" />
//                 <Line type="monotone" dataKey="sessions" stroke="#2ecc71" strokeWidth={2} name="Sessions" />
//               </LineChart>
//             </ResponsiveContainer>
//           </ChartCard>
//         </Grid>

//         <Grid item xs={12} md={4}>
//           <ChartCard title="Animal Health Status" height={300}>
//             <ResponsiveContainer>
//               <PieChart>
//                 <Pie
//                   data={chartData.animalHealth}
//                   dataKey="value"
//                   nameKey="name"
//                   cx="50%"
//                   cy="50%"
//                   outerRadius={80}
//                   label={({ name, value }) => `${name}: ${value}`}
//                 >
//                   {chartData.animalHealth.map((entry, index) => (
//                     <Cell key={index} fill={entry.color} />
//                   ))}
//                 </Pie>
//                 <Tooltip />
//                 <Legend />
//               </PieChart>
//             </ResponsiveContainer>
//           </ChartCard>
//         </Grid>

//         <Grid item xs={12} md={4}>
//           <ChartCard title="Animal Status Breakdown" height={250}>
//             <ResponsiveContainer>
//               <PieChart>
//                 <Pie
//                   data={chartData.animalBreakdown}
//                   dataKey="value"
//                   nameKey="name"
//                   cx="50%"
//                   cy="50%"
//                   outerRadius={70}
//                   label={({ name, value }) => `${name}: ${value}`}
//                 >
//                   {chartData.animalBreakdown.map((entry, index) => (
//                     <Cell key={index} fill={entry.color} />
//                   ))}
//                 </Pie>
//                 <Tooltip />
//                 <Legend />
//               </PieChart>
//             </ResponsiveContainer>
//           </ChartCard>
//         </Grid>

//         <Grid item xs={12} md={4}>
//           <ChartCard title="Top Medication Usage" height={250}>
//             <ResponsiveContainer>
//               <BarChart data={chartData.medicationUsage}>
//                 <CartesianGrid strokeDasharray="3 3" />
//                 <XAxis dataKey="name" />
//                 <YAxis />
//                 <Tooltip />
//                 <Bar dataKey="usage" fill="#9b59b6" name="Quantity Used" radius={[4, 4, 0, 0]} />
//               </BarChart>
//             </ResponsiveContainer>
//           </ChartCard>
//         </Grid>

//         <Grid item xs={12} md={4}>
//           <ChartCard title="Reproduction Events" height={250}>
//             <ResponsiveContainer>
//               <PieChart>
//                 <Pie
//                   data={chartData.reproductionStats}
//                   dataKey="value"
//                   nameKey="name"
//                   cx="50%"
//                   cy="50%"
//                   outerRadius={70}
//                   label={({ name, value }) => `${name}: ${value}`}
//                 >
//                   {chartData.reproductionStats.map((entry, index) => (
//                     <Cell key={index} fill={COLORS[index % COLORS.length]} />
//                   ))}
//                 </Pie>
//                 <Tooltip />
//                 <Legend />
//               </PieChart>
//             </ResponsiveContainer>
//           </ChartCard>
//         </Grid>
//       </Grid>
//     </Box>
//   );
// };

// export default DashboardHome;
